import { inject } from '@angular/core';
import { ResolveFn } from '@angular/router';
import { Observable, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { HttpErrorResponse } from '@angular/common/http';

import { ConfiguracionService } from '../services/configuracion.service';
import { OwnershipTransferDTO } from '../models/configuracion.model';

/** Tipo que discrimina el resultado de la pre-validación del token */
export type TransferenciaErrorType = 'wrong-user' | 'invalid' | null;

/** Resultado que el componente recibe como dato resuelto de la ruta */
export interface TransferenciaResolverData {
  transfer: OwnershipTransferDTO | null;
  errorType: TransferenciaErrorType;
}

/**
 * Resolver que pre-valida el token de transferencia de propiedad antes de renderizar
 * la página de confirmación.
 *
 * Realiza un GET /{token}/info (lectura, sin modificar estado) y normaliza cualquier
 * error HTTP en un resultado tipado para que el componente pueda renderizar
 * el estado correcto de forma inmediata — sin loading spinner.
 *
 * Posibles resultados:
 * - `{ transfer: DTO, errorType: null }` → token válido, usuario correcto → estado `pending`
 * - `{ transfer: null, errorType: 'wrong-user' }` → 403 BUSINESS_RULE → estado `wrong-user`
 * - `{ transfer: null, errorType: 'invalid' }` → 404/409/410/otro → estado `error`
 *
 * El resolver NUNCA lanza errores (usa catchError → of) para que Angular siempre
 * active la ruta y el componente pueda mostrar la UI correcta.
 */
export const transferenciaConfirmarResolver: ResolveFn<TransferenciaResolverData> =
  (route): Observable<TransferenciaResolverData> => {
    const service = inject(ConfiguracionService);
    const token   = route.queryParamMap.get('token')!; // garantizado por el guard

    return service.getTransferInfo(token).pipe(
      map((transfer): TransferenciaResolverData => ({ transfer, errorType: null })),
      catchError((err: HttpErrorResponse): Observable<TransferenciaResolverData> => {
        const isWrongUser =
          err.status === 403 && err.error?.operation === 'BUSINESS_RULE';

        return of({
          transfer: null,
          errorType: isWrongUser ? 'wrong-user' : 'invalid',
        });
      })
    );
  };
